/**
 * Shared print utilities for PDF export functionality
 * Used by both Aero Reports and Tyre Reports
 */

import { toPng } from 'html-to-image';

// Declare Plotly type for toImage function
declare const Plotly: {
  toImage: (
    gd: HTMLElement,
    opts: { format: string; width: number; height: number }
  ) => Promise<string>;
};

export interface ConvertChartsOptions {
  /** Ratio of vertical image to keep (0-1). Default: 1 (full height, no clipping) */
  leafletClipRatio?: number;
  /** Display width percentage (0-100). Default: 100 */
  leafletWidthPercent?: number;
}

/**
 * Prepare charts for print by cloning SVGs and removing interactive elements.
 * Handles both ApexCharts and Plotly charts.
 * Keeps SVG as vector for crisp text quality.
 * @param options Configuration options for chart conversion
 * @returns Array of inserted elements that need to be cleaned up after print
 */
export async function convertChartsForPrint(options: ConvertChartsOptions = {}): Promise<Element[]> {
  const { leafletClipRatio = 1, leafletWidthPercent = 100 } = options;
  const insertedElements: Element[] = [];

  // Handle ApexCharts
  const apexChartContainers = document.querySelectorAll('.apexcharts-canvas');
  for (let i = 0; i < apexChartContainers.length; i++) {
    const container = apexChartContainers[i] as HTMLElement;

    try {
      const svgElement = container.querySelector('svg.apexcharts-svg');
      if (!svgElement) continue;

      const chartWrapper = container.parentElement;
      if (!chartWrapper) continue;

      const rect = container.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) continue;

      const svgClone = svgElement.cloneNode(true) as SVGElement;
      svgClone.setAttribute('width', String(rect.width));
      svgClone.setAttribute('height', String(rect.height));
      svgClone.style.width = `${rect.width}px`;
      svgClone.style.height = `${rect.height}px`;

      // Remove toolbar and interactive elements from the clone
      const toolbarElements = svgClone.querySelectorAll(
        '.apexcharts-toolbar, .apexcharts-menu, .apexcharts-zoom-icon, ' +
          '.apexcharts-zoomin-icon, .apexcharts-zoomout-icon, .apexcharts-reset-icon, ' +
          '.apexcharts-pan-icon, .apexcharts-selection-icon, .apexcharts-menu-icon, ' +
          '.apexcharts-toolbar-custom-icon'
      );
      toolbarElements.forEach((el) => el.remove());

      svgClone.setAttribute('data-print-svg', 'true');
      svgClone.style.display = 'block';
      svgClone.style.maxWidth = '100%';

      container.setAttribute('data-print-hidden', 'true');
      container.style.display = 'none';

      chartWrapper.appendChild(svgClone);
      insertedElements.push(svgClone);
    } catch (error) {
      console.warn('Failed to prepare ApexChart for print:', error);
    }
  }

  // Handle Plotly charts - convert to static SVG images using Plotly.toImage()
  // Using SVG format preserves font references for proper rendering
  const plotlyContainers = document.querySelectorAll('.js-plotly-plot');
  for (let i = 0; i < plotlyContainers.length; i++) {
    const container = plotlyContainers[i] as HTMLElement;

    try {
      const rect = container.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) continue;

      // Check if Plotly is available
      if (typeof Plotly === 'undefined' || !Plotly.toImage) {
        console.warn('Plotly.toImage not available');
        continue;
      }

      // Convert Plotly chart to SVG image (preserves fonts better than PNG)
      const imageData = await Plotly.toImage(container, {
        format: 'svg',
        width: Math.round(rect.width),
        height: Math.round(rect.height),
      });

      // Decode the SVG data URL and inject Google Fonts import
      let svgContent = imageData;
      if (imageData.startsWith('data:image/svg+xml,')) {
        const svgDecoded = decodeURIComponent(imageData.replace('data:image/svg+xml,', ''));
        // Inject Google Fonts import and font-family fallback into SVG
        const fontStyle = '<defs><style type="text/css">@import url(\'https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&amp;display=swap\'); text { font-family: \'Poppins\', sans-serif !important; }</style></defs>';
        const svgWithFont = svgDecoded.replace(/<svg([^>]*)>/, `<svg$1>${fontStyle}`);
        svgContent = 'data:image/svg+xml,' + encodeURIComponent(svgWithFont);
      }

      // Create an img element with the chart image
      const img = document.createElement('img');
      img.src = svgContent;
      img.style.width = `${rect.width}px`;
      img.style.height = `${rect.height}px`;
      img.style.display = 'block';
      img.setAttribute('data-print-img', 'true');

      // Get the parent wrapper and insert the image
      const chartWrapper = container.parentElement;
      if (!chartWrapper) continue;

      // Hide the original Plotly container
      container.setAttribute('data-print-hidden', 'true');
      container.style.display = 'none';

      // Insert the static image
      chartWrapper.appendChild(img);
      insertedElements.push(img);
    } catch (error) {
      console.warn('Failed to prepare Plotly chart for print:', error);
    }
  }

  // Handle Leaflet maps - convert to static PNG images using html-to-image
  // Leaflet has complex tile layers and SVG overlays that don't clone well
  const leafletContainers = document.querySelectorAll('.leaflet-container');
  for (let i = 0; i < leafletContainers.length; i++) {
    const container = leafletContainers[i] as HTMLElement;

    try {
      const rect = container.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) continue;

      // Use html-to-image to capture the map with all layers
      const imageData = await toPng(container, {
        width: Math.round(rect.width),
        height: Math.round(rect.height),
        cacheBust: true,
        // Skip attribution links to avoid CORS issues
        filter: (node: HTMLElement) => {
          if (node.classList?.contains('leaflet-control-attribution')) {
            return false;
          }
          return true;
        },
      });

      // Optionally clip the image using canvas
      const tempImg = new Image();
      tempImg.src = imageData;
      await new Promise((resolve) => {
        tempImg.onload = resolve;
      });

      let finalImageData = imageData;
      const finalWidth = tempImg.width;

      if (leafletClipRatio < 1) {
        // Calculate clipped dimensions based on ratio
        const clippedHeight = Math.round(tempImg.height * leafletClipRatio);
        const clippedCanvas = document.createElement('canvas');
        clippedCanvas.width = tempImg.width;
        clippedCanvas.height = clippedHeight;

        const ctx = clippedCanvas.getContext('2d');
        if (ctx) {
          // Draw only the top portion of the image
          ctx.drawImage(
            tempImg,
            0, 0, tempImg.width, clippedHeight, // Source: top portion
            0, 0, tempImg.width, clippedHeight // Destination: same size
          );
          finalImageData = clippedCanvas.toDataURL('image/png');
        }
      }

      // Create a wrapper div for the map image
      const leafletWrapper = document.createElement('div');
      leafletWrapper.style.width = `${finalWidth}px`;
      leafletWrapper.style.display = 'block';
      leafletWrapper.setAttribute('data-print-leaflet', 'true');

      // Create an img element with the map image
      const leafletImg = document.createElement('img');
      leafletImg.src = finalImageData;
      leafletImg.style.width = `${leafletWidthPercent}%`;
      leafletImg.style.height = 'auto';
      leafletImg.style.display = 'block';

      leafletWrapper.appendChild(leafletImg);

      // Get the parent wrapper and insert the image
      const mapWrapper = container.parentElement;
      if (!mapWrapper) continue;

      // Hide the original Leaflet container
      container.setAttribute('data-print-hidden', 'true');
      container.style.display = 'none';

      // Insert the leaflet image wrapper
      mapWrapper.appendChild(leafletWrapper);
      insertedElements.push(leafletWrapper);
    } catch (error) {
      console.warn('Failed to prepare Leaflet map for print:', error);
    }
  }

  return insertedElements;
}

/**
 * Restore charts after print by removing inserted elements and showing original charts
 * @param insertedElements Array of elements (SVGs/images) that were inserted for print
 */
export function restoreCharts(insertedElements: Element[]): void {
  // Remove all inserted print elements (SVGs and images)
  for (const el of insertedElements) {
    el.remove();
  }

  // Show all hidden chart elements
  const hiddenElements = document.querySelectorAll('[data-print-hidden="true"]');
  for (const el of hiddenElements) {
    (el as HTMLElement).style.display = '';
    el.removeAttribute('data-print-hidden');
  }
}

export interface PrintOptions {
  /** Wait time in milliseconds before printing (default: 2000) */
  waitTime?: number;
  /** Use landscape orientation (default: false) */
  landscape?: boolean;
  /** Callback before print */
  onBeforePrint?: () => void;
  /** Callback after print */
  onAfterPrint?: () => void;
}

/**
 * Execute print with chart conversion
 * @param containerRef Reference to the print container element
 * @param options Print options including wait time and orientation
 */
export async function executePrint(
  containerRef: React.RefObject<HTMLDivElement>,
  options: PrintOptions = {}
): Promise<void> {
  const { waitTime = 2000, landscape = false, onBeforePrint, onAfterPrint } = options;

  onBeforePrint?.();

  // Yield to allow spinner to render before heavy DOM work
  await new Promise((resolve) => setTimeout(resolve, 50));

  // Set fixed width for consistent chart rendering (but NOT fixed height - content spans multiple pages)
  const container = containerRef.current;
  const originalStyles = {
    width: container?.style.width || '',
    minWidth: container?.style.minWidth || '',
    maxWidth: container?.style.maxWidth || '',
    overflow: container?.style.overflow || '',
  };

  // A4 width for print (landscape or portrait) - height is auto for multi-page content
  const printSizeWidth = landscape ? '1132px' : '800px';

  if (container) {
    container.style.width = printSizeWidth;
    container.style.minWidth = printSizeWidth;
    container.style.maxWidth = printSizeWidth;
    container.style.overflow = 'visible';

    // Add landscape class if needed
    if (landscape) {
      container.classList.add('print-landscape');
    }
  }

  // Dispatch resize event to trigger ApexCharts redraw
  window.dispatchEvent(new Event('resize'));

  // Wait for charts to redraw
  await new Promise((resolve) => setTimeout(resolve, waitTime));

  // Prepare charts for print (convert to static images)
  const insertedElements = await convertChartsForPrint();

  // Small delay to ensure images are rendered
  await new Promise((resolve) => setTimeout(resolve, 1000));

  // Print
  window.print();

  // Restore original styles and charts after a delay
  setTimeout(() => {
    if (container) {
      container.style.width = originalStyles.width;
      container.style.minWidth = originalStyles.minWidth;
      container.style.maxWidth = originalStyles.maxWidth;
      container.style.overflow = originalStyles.overflow;

      // Remove landscape class
      container.classList.remove('print-landscape');
    }
    restoreCharts(insertedElements);
    onAfterPrint?.();
  }, 500);
}

/**
 * CSS styles for the exported HTML - a simplified, self-contained stylesheet
 */
const EXPORT_CSS = `
/* Base styles */
* { margin: 0; padding: 0; box-sizing: border-box; }
body {
  font-family: 'Poppins', sans-serif;
  font-size: 11pt;
  line-height: 1.4;
  color: #1e3a5f;
  background: #f5f5f5;
  padding: 20px;
}

/* Layout */
.cr-w-full { width: 100%; }
.cr-space-y-8 > * + * { margin-top: 2rem; }
.cr-space-y-4 > * + * { margin-top: 1rem; }
.cr-space-y-2 > * + * { margin-top: 0.5rem; }
.cr-space-y-1 > * + * { margin-top: 0.25rem; }
.cr-mb-4 { margin-bottom: 1rem; }
.cr-mb-6 { margin-bottom: 1.5rem; }
.cr-mt-6 { margin-top: 1.5rem; }
.cr-mt-8 { margin-top: 2rem; }
.cr-p-6 { padding: 1.5rem; }
.cr-px-6 { padding-left: 1.5rem; padding-right: 1.5rem; }
.cr-py-2 { padding-top: 0.5rem; padding-bottom: 0.5rem; }
.cr-py-3 { padding-top: 0.75rem; padding-bottom: 0.75rem; }
.cr-py-4 { padding-top: 1rem; padding-bottom: 1rem; }
.cr-pl-6 { padding-left: 1.5rem; }
.cr-pl-10 { padding-left: 2.5rem; }
.cr-mr-6 { margin-right: 1.5rem; }
.cr-ml-2 { margin-left: 0.5rem; }
.cr-gap-1 { gap: 0.25rem; }
.cr-gap-2 { gap: 0.5rem; }
.cr-gap-3 { gap: 0.75rem; }
.cr-gap-4 { gap: 1rem; }
.cr-gap-6 { gap: 1.5rem; }
.cr-gap-8 { gap: 2rem; }
.cr-gap-x-1 { column-gap: 0.25rem; }
.cr-gap-x-2 { column-gap: 0.5rem; }
.cr-gap-x-4 { column-gap: 1rem; }
.cr-gap-y-2 { row-gap: 0.5rem; }
.cr-gap-y-4 { row-gap: 1rem; }

/* Flexbox */
.cr-flex { display: flex; }
.cr-flex-row { flex-direction: row; }
.cr-flex-col { flex-direction: column; }
.cr-flex-wrap { flex-wrap: wrap; }
.cr-flex-1 { flex: 1 1 0%; }
.cr-items-center { align-items: center; }
.cr-items-start { align-items: flex-start; }
.cr-items-end { align-items: flex-end; }
.cr-justify-between { justify-content: space-between; }
.cr-justify-center { justify-content: center; }
.cr-justify-start { justify-content: flex-start; }
.cr-justify-end { justify-content: flex-end; }

/* Grid */
.cr-grid { display: grid; }
.cr-grid-cols-1 { grid-template-columns: repeat(1, minmax(0, 1fr)); }
.cr-grid-cols-2 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
.cr-grid-cols-3 { grid-template-columns: repeat(3, minmax(0, 1fr)); }
.cr-grid-cols-4 { grid-template-columns: repeat(4, minmax(0, 1fr)); }
.cr-grid-cols-\\[1fr_3fr_1fr_1fr_1fr_1fr\\] { grid-template-columns: 1fr 3fr 1fr 1fr 1fr 1fr; }
.cr-grid-cols-\\[65\\%_35\\%\\] { grid-template-columns: 65% 35%; }
.lg\\:cr-grid-cols-2 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
.lg\\:cr-grid-cols-3 { grid-template-columns: repeat(3, minmax(0, 1fr)); }
.lg\\:cr-grid-cols-\\[65\\%_35\\%\\] { grid-template-columns: 65% 35%; }
.xl\\:cr-grid-cols-3 { grid-template-columns: repeat(3, minmax(0, 1fr)); }
.xl\\:cr-grid-cols-4 { grid-template-columns: repeat(4, minmax(0, 1fr)); }

/* Typography */
.cr-text-2xl { font-size: 1.5rem; font-weight: bold; }
.cr-text-xl { font-size: 1.25rem; }
.cr-text-lg { font-size: 1.125rem; }
.cr-text-sm { font-size: 0.875rem; }
.cr-text-xs { font-size: 0.75rem; }
.cr-font-bold { font-weight: 700; }
.cr-font-semibold { font-weight: 600; }
.cr-font-medium { font-weight: 500; }
.cr-text-gray-500 { color: #6b7280; }
.cr-text-gray-600 { color: #4b5563; }
.cr-text-gray-700 { color: #374151; }
.cr-text-gray-800 { color: #1f2937; }
.cr-text-gray-900 { color: #111827; }
.cr-text-blue-600 { color: #2563eb; }
.cr-text-green-600 { color: #16a34a; }
.cr-text-red-600 { color: #dc2626; }
.cr-uppercase { text-transform: uppercase; }
.cr-tracking-wider { letter-spacing: 0.05em; }
.cr-text-center { text-align: center; }
.cr-text-left { text-align: left; }
.cr-whitespace-nowrap { white-space: nowrap; }

/* Backgrounds and borders */
.cr-bg-white { background-color: white; }
.cr-bg-gray-50 { background-color: #f9fafb; }
.cr-bg-blue-50 { background-color: #eff6ff; }
.cr-rounded-lg { border-radius: 0.5rem; }
.cr-shadow { box-shadow: 0 1px 3px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.06); }
.cr-shadow-sm { box-shadow: 0 1px 2px rgba(0,0,0,0.05); }
.cr-border { border: 1px solid #e5e7eb; }
.cr-border-b { border-bottom: 1px solid #e5e7eb; }
.cr-border-gray-200 { border-color: #e5e7eb; }
.cr-divide-y > * + * { border-top: 1px solid #e5e7eb; }
.cr-divide-gray-200 > * + * { border-color: #e5e7eb; }

/* Tables */
table { width: 100%; border-collapse: collapse; }
th, td { padding: 0.75rem 1rem; text-align: left; border: 1px solid #e5e7eb; }
thead { background-color: #f9fafb; }
th { font-weight: 600; font-size: 0.75rem; text-transform: uppercase; color: #6b7280; }
tbody tr:nth-child(even) { background-color: #f9fafb; }
.cr-min-w-full { min-width: 100%; }

/* Screen data table (print data table styles) */
.screen-data-table { display: block; margin-top: 1rem; overflow-x: auto; }
.screen-data-table .print-data-table-page { margin-bottom: 1.5rem; }
.screen-data-table .print-section-header {
  font-size: 1rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 0.75rem;
}
.screen-data-table .print-data-table-content {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.75rem;
  table-layout: auto;
}
.screen-data-table .print-data-table-content th,
.screen-data-table .print-data-table-content td {
  border: 1px solid #e5e7eb;
  padding: 0.25rem 0.5rem;
  text-align: center;
  vertical-align: middle;
}
.screen-data-table .print-data-table-content th {
  background-color: #03365d;
  color: white;
  font-weight: 600;
  font-size: 0.65rem;
  text-transform: none;
}
.screen-data-table .print-data-table-content .print-divider-right {
  border-right: 2px solid #9ca3af;
}
.screen-data-table .print-config-row td {
  background-color: #dbeafe;
  font-weight: 600;
}
.screen-data-table .print-run-row td {
  background-color: #f9fafb;
}
.screen-data-table .print-run-row:nth-child(even) td {
  background-color: #f3f4f6;
}

/* Images (charts) */
img { max-width: 100%; height: auto; display: block; }
.chart-image { margin: 1rem 0; }
.leaflet-export-image { margin: 1rem 0; }

/* Hide elements */
.screen-only, button, select, input, nav[aria-label="Tabs"] { display: none !important; }

/* Print header/section styling */
.print-section-header {
  font-size: 1.25rem;
  font-weight: bold;
  color: #03365d;
  margin-top: 2rem;
  margin-bottom: 1rem;
  padding-bottom: 0.25rem;
  border-bottom: 1px solid #ccc;
}

/* Logo */
.print-logo {
  position: absolute;
  top: 10px;
  left: 10px;
  padding: 10px;
  z-index: 10;
}
.print-logo img { width: 180px; height: auto; }
.overflow-auto { overflow: visible; position: relative; padding-top: 60px; }

/* Page breaks for print */
@media print {
  .print-page-break-before { page-break-before: always; }
  .print-avoid-break { page-break-inside: avoid; }
}
`;

export interface ExportHtmlOptions {
  /** Wait time in milliseconds before capturing (default: 2000) */
  waitTime?: number;
  /** Filename for the exported HTML (default: 'report.html') */
  filename?: string;
  /** Callback before export starts */
  onBeforeExport?: () => void;
  /** Callback after export completes */
  onAfterExport?: () => void;
  /** Ratio of vertical image to keep (0-1). Default: 1 (full height, no clipping) */
  leafletClipRatio?: number;
  /** Display width percentage (0-100). Default: 100 */
  leafletWidthPercent?: number;
}

/**
 * Export the container content as a self-contained HTML file with static chart images
 * @param containerRef Reference to the container element to export
 * @param options Export options
 */
export async function executeExportHtml(
  containerRef: React.RefObject<HTMLDivElement>,
  options: ExportHtmlOptions = {}
): Promise<void> {
  const { waitTime = 2000, filename = 'report.html', onBeforeExport, onAfterExport, leafletClipRatio = 1, leafletWidthPercent = 100 } = options;

  onBeforeExport?.();

  // Yield to allow spinner to render before heavy DOM work
  await new Promise((resolve) => setTimeout(resolve, 50));

  const container = containerRef.current;
  if (!container) {
    console.error('Export container not found');
    onAfterExport?.();
    return;
  }

  // Store original styles
  const originalStyles = {
    width: container.style.width || '',
    minWidth: container.style.minWidth || '',
    maxWidth: container.style.maxWidth || '',
    overflow: container.style.overflow || '',
  };

  // Set fixed width for consistent chart rendering
  const exportWidth = '1000px';
  container.style.width = exportWidth;
  container.style.minWidth = exportWidth;
  container.style.maxWidth = exportWidth;
  container.style.overflow = 'visible';

  // Dispatch resize event to trigger chart redraw
  window.dispatchEvent(new Event('resize'));

  // Wait for charts to redraw
  await new Promise((resolve) => setTimeout(resolve, waitTime));

  // Convert charts to static images
  const insertedElements = await convertChartsForPrint({ leafletClipRatio, leafletWidthPercent });

  // Small delay to ensure images are rendered
  await new Promise((resolve) => setTimeout(resolve, 1000));

  try {
    // Clone the container content
    const clonedContent = container.cloneNode(true) as HTMLElement;

    // Remove screen-only elements from clone
    const screenOnlyElements = clonedContent.querySelectorAll('.screen-only, button, select, input');
    screenOnlyElements.forEach((el) => el.remove());

    // Remove original chart containers (we keep the static images)
    const hiddenCharts = clonedContent.querySelectorAll('[data-print-hidden="true"]');
    hiddenCharts.forEach((el) => el.remove());

    // Remove data attributes from images
    const printImages = clonedContent.querySelectorAll('[data-print-img="true"]');
    printImages.forEach((img) => {
      img.removeAttribute('data-print-img');
      img.classList.add('chart-image');
    });

    // Remove data attributes from Leaflet map wrappers
    const leafletWrappers = clonedContent.querySelectorAll('[data-print-leaflet="true"]');
    leafletWrappers.forEach((wrapper) => {
      wrapper.removeAttribute('data-print-leaflet');
      wrapper.classList.add('leaflet-export-image');
    });

    // Remove data attributes from SVGs
    const printSvgs = clonedContent.querySelectorAll('[data-print-svg="true"]');
    printSvgs.forEach((svg) => {
      svg.removeAttribute('data-print-svg');
    });

    // Get the logo image and convert to base64 if present
    const logoImg = clonedContent.querySelector('.print-logo img') as HTMLImageElement;
    if (logoImg && logoImg.src) {
      try {
        // Fetch the logo and convert to base64
        const response = await fetch(logoImg.src);
        const blob = await response.blob();
        const base64 = await new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.readAsDataURL(blob);
        });
        logoImg.src = base64;
      } catch (e) {
        console.warn('Failed to convert logo to base64:', e);
      }
    }

    // Build the complete HTML document
    const htmlContent = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Tyre Report - ${new Date().toLocaleDateString()}</title>
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap" rel="stylesheet">
  <style>
${EXPORT_CSS}
  </style>
</head>
<body>
${clonedContent.outerHTML}
</body>
</html>`;

    // Create and trigger download
    const blob = new Blob([htmlContent], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Failed to export HTML:', error);
  }

  // Restore original styles and charts
  container.style.width = originalStyles.width;
  container.style.minWidth = originalStyles.minWidth;
  container.style.maxWidth = originalStyles.maxWidth;
  container.style.overflow = originalStyles.overflow;
  restoreCharts(insertedElements);

  onAfterExport?.();
}
